import { useState, useRef } from 'react'
import { X, Upload, Loader2, Image as ImageIcon } from 'lucide-react'
import Button from '../Button'
import Input from '../Input'
import { api } from '../../services/api'
import { getBackendUrl } from '../../utils/backendUrl'
import MediaSelector from '../MediaSelector'

export default function ImageInsertDialog({ onInsert, onClose }) {
  const [view, setView] = useState('upload')
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [altText, setAltText] = useState('')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [showMediaSelector, setShowMediaSelector] = useState(false)
  const fileInputRef = useRef(null)

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile) {
      if (!selectedFile.type.startsWith('image/')) {
        setError('Please select an image file')
        return
      }
      setFile(selectedFile)
      setError(null)
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreview(reader.result)
      }
      reader.readAsDataURL(selectedFile)
    }
  }

  const handleUpload = async () => {
    if (!file) {
      setError('Please select an image')
      return
    }

    setUploading(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post('/media/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      const imageUrl = response.data.data.url

      let fullImageUrl = imageUrl
      if (!imageUrl.startsWith('http')) {
        fullImageUrl = `${getBackendUrl()}${imageUrl}`
      }

      onInsert(fullImageUrl, altText)
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload image')
    } finally {
      setUploading(false)
    }
  }

  if (showMediaSelector) {
    return (
      <MediaSelector
        onSelect={(url, media) => {
          const fullUrl = url.startsWith('http')
            ? url
            : `${getBackendUrl()}${url}`
          onInsert(fullUrl, media?.alt_text || altText)
          onClose()
        }}
        onClose={() => setShowMediaSelector(false)}
      />
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Insert Image</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={uploading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex border-b">
          <button
            onClick={() => setView('upload')}
            className={`flex-1 px-4 py-2 text-sm font-medium ${
              view === 'upload'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Upload className="h-4 w-4 inline mr-2" />
            Upload New
          </button>
          <button
            onClick={() => setView('gallery')}
            className={`flex-1 px-4 py-2 text-sm font-medium ${
              view === 'gallery'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <ImageIcon className="h-4 w-4 inline mr-2" />
            Choose from Gallery
          </button>
        </div>

        <div className="p-4 space-y-4">
          {view === 'gallery' ? (
            <div className="text-center py-8">
              <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">
                Choose an image from your library
              </p>
              <Button
                onClick={() => setShowMediaSelector(true)}
                className="w-full"
              >
                <ImageIcon className="h-4 w-4 mr-2" />
                Browse Gallery
              </Button>
            </div>
          ) : (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="secondary"
                className="w-full"
                disabled={uploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                Choose Image
              </Button>
            </div>
          )}

          {view === 'upload' && preview && (
            <div className="border rounded-lg p-2">
              <img
                src={preview}
                alt="Preview"
                className="w-full h-auto rounded"
              />
            </div>
          )}

          {view === 'upload' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alt Text
              </label>
              <Input
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                placeholder="Describe the image"
                disabled={uploading}
              />
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}
        </div>

        {view === 'upload' && (
          <div className="flex justify-end gap-2 p-4 border-t">
            <Button variant="secondary" onClick={onClose} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={!file || uploading}>
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                'Insert'
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
